"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useGenieStageContainer } from "@/lib/genie/stage-container";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { AdminService, UsersService, GroupOrder } from "@/generated";
import {
  AdminService as AdminServiceCustom,
  GoToLinkResponse,
} from "@/services/Admin/admin";
import type { Tenant, GroupWithUsers, FeatureFlags } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { Input } from "@/components/shadcn/input";
import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import { Skeleton } from "@/components/shadcn/skeleton";
import { ScrollArea } from "@/components/shadcn/scroll-area";
import { cn } from "@/lib/utils";
import { ACTIVE_GROUP_KEY } from "@/utils/activeGroup";
import {
  Rocket,
  Search,
  ChevronLeft,
  Building2,
  FolderOpen,
  X,
  Loader2,
  Ghost,
  Star,
  StarOff,
  Plus,
  Pencil,
  Check,
  Trash2,
  AlertTriangle,
  Flag,
  Link2,
  Copy,
  UserCheck,
} from "lucide-react";
import { Switch } from "@/components/shadcn/switch";
import { SpotToast } from "@/components/SpotToast";
import { NoxusEntityIcon } from "@/components/UI/NoxusEntityIcon";

type ToolbarView =
  | "favorites"
  | "search-tenants"
  | "search-workspaces"
  | "feature-flags"
  | "go-to-link";

interface Favorite {
  id: string;
  name: string;
  tenantId: string;
  tenantName: string;
  groupId: string;
  groupName: string;
}

const STORAGE_KEY = "noxus-deployment-toolbar-favorites";
const POSITION_KEY = "noxus-deployment-toolbar-position";
export const TOOLBAR_HIDDEN_KEY = "noxus-deployment-toolbar-hidden";
export const TOOLBAR_VISIBILITY_EVENT = "noxus:toolbarVisibilityChanged";
export const HARDCORE_KEY = "noxus-deployment-toolbar-hardcore";
const MEGA_KEY = "noxus-deployment-toolbar-mega";
const MEGA_CLICKS_NEEDED = 10;
export const HARDCORE_CHANGED_EVENT = "noxus:hardcoreGhostChanged";

const CONFETTI_COLORS = ["#ff0000", "#cc0000", "#ff3333", "#ff6666", "#990000"];

async function invalidateGhostQueries(
  queryClient: ReturnType<typeof useQueryClient>
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["USER"] }),
    queryClient.invalidateQueries({ queryKey: ["GROUP"] }),
    queryClient.invalidateQueries({ queryKey: ["tenants"] }),
  ]);
}

function loadFavorites(): Favorite[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Favorite[]) : [];
  } catch {
    return [];
  }
}

function saveFavorites(favorites: Favorite[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
}

function loadPosition(): { x: number; y: number } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { xPct: number; yPct: number };
    // Convert percentages back to pixels, clamped to viewport
    if (saved.xPct !== undefined && saved.yPct !== undefined) {
      return {
        x: Math.max(
          20,
          Math.min(window.innerWidth - 68, saved.xPct * window.innerWidth)
        ),
        y: Math.max(
          20,
          Math.min(window.innerHeight - 68, saved.yPct * window.innerHeight)
        ),
      };
    }
    // Legacy pixel format — clear it
    localStorage.removeItem(POSITION_KEY);
    return null;
  } catch {
    return null;
  }
}

function savePosition(pos: { x: number; y: number }) {
  if (typeof window === "undefined") return;
  // Store as percentages so it survives window resizes and different worktrees
  localStorage.setItem(
    POSITION_KEY,
    JSON.stringify({
      xPct: pos.x / window.innerWidth,
      yPct: pos.y / window.innerHeight,
    })
  );
}

export function useDraggable(initialPos: { x: number; y: number }) {
  const [position, setPosition] = useState(initialPos);
  const dragState = useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    moved: boolean;
  } | null>(null);
  const isDragging = useRef(false);

  const clamp = useCallback((pos: { x: number; y: number }) => {
    const margin = 20;
    return {
      x: Math.max(margin, Math.min(window.innerWidth - margin - 48, pos.x)),
      y: Math.max(margin, Math.min(window.innerHeight - margin - 48, pos.y)),
    };
  }, []);

  useEffect(() => {
    const onResize = () => setPosition((pos) => loadPosition() ?? clamp(pos));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clamp]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest("[data-toolbar-panel]")) return;

      dragState.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: position.x,
        startPosY: position.y,
        moved: false,
      };
      isDragging.current = false;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [position]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragState.current) return;
      const dx = e.clientX - dragState.current.startX;
      const dy = e.clientY - dragState.current.startY;

      if (!dragState.current.moved && Math.abs(dx) + Math.abs(dy) > 4) {
        dragState.current.moved = true;
        isDragging.current = true;
      }

      if (dragState.current.moved) {
        const newPos = clamp({
          x: dragState.current.startPosX + dx,
          y: dragState.current.startPosY + dy,
        });
        setPosition(newPos);
      }
    },
    [clamp]
  );

  const onPointerUp = useCallback(() => {
    if (dragState.current?.moved) {
      savePosition(position);
    }
    setTimeout(() => {
      isDragging.current = false;
    }, 0);
    dragState.current = null;
  }, [position]);

  return { position, isDragging, onPointerDown, onPointerMove, onPointerUp };
}

export const DeploymentToolbar = () => {
  const { spotUser, isGhosting, spotTenant, spotGroup, setActiveGroupId } =
    useSpotUser();
  const queryClient = useQueryClient();
  const stageContainer = useGenieStageContainer();

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<ToolbarView>("favorites");
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [search, setSearch] = useState("");
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [hardcoreMode, setHardcoreMode] = useState(false);
  const [megaMode, setMegaMode] = useState(false);
  const hardcoreClickCount = useRef(0);
  const hardcoreClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hardcoreCooldown = useRef(false);
  const confettiInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFavorites(loadFavorites());
    setHardcoreMode(localStorage.getItem(HARDCORE_KEY) === "true");
    setMegaMode(localStorage.getItem(MEGA_KEY) === "true");
  }, []);

  const [initialPos] = useState(
    () =>
      loadPosition() ?? {
        x: typeof window !== "undefined" ? window.innerWidth - 72 : 0,
        y: typeof window !== "undefined" ? window.innerHeight - 72 : 0,
      }
  );
  const { position, isDragging, onPointerDown, onPointerMove, onPointerUp } =
    useDraggable(initialPos);

  const isHardcore = isGhosting && hardcoreMode;
  const isMega = isGhosting && megaMode;
  const isNoxusEmail = spotUser?.email?.endsWith("@noxus.ai") ?? false;

  useEffect(() => {
    if (!isMega) {
      if (confettiInterval.current) {
        clearInterval(confettiInterval.current);
        confettiInterval.current = null;
      }
      return;
    }

    const fireConfetti = async () => {
      const confetti = (await import("canvas-confetti")).default;
      confetti({
        particleCount: 40,
        spread: 120,
        origin: { x: Math.random(), y: Math.random() },
        colors: CONFETTI_COLORS,
        gravity: 0.6,
        ticks: 200,
        startVelocity: 30,
        disableForReducedMotion: true,
      });
      confetti({
        particleCount: 25,
        spread: 100,
        origin: { x: Math.random(), y: Math.random() },
        colors: CONFETTI_COLORS,
        gravity: 0.6,
        ticks: 200,
        startVelocity: 25,
        disableForReducedMotion: true,
      });
    };

    void fireConfetti();
    confettiInterval.current = setInterval(fireConfetti, 500);

    return () => {
      if (confettiInterval.current) {
        clearInterval(confettiInterval.current);
        confettiInterval.current = null;
      }
    };
  }, [isMega]);

  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    setIsHidden(localStorage.getItem(TOOLBAR_HIDDEN_KEY) === "true");

    const onCustomToggle = () =>
      setIsHidden(localStorage.getItem(TOOLBAR_HIDDEN_KEY) === "true");
    const onStorageEvent = (e: StorageEvent) => {
      if (e.key === TOOLBAR_HIDDEN_KEY || e.key === null) {
        setIsHidden(localStorage.getItem(TOOLBAR_HIDDEN_KEY) === "true");
      }
    };
    window.addEventListener("storage", onStorageEvent);
    window.addEventListener(TOOLBAR_VISIBILITY_EVENT, onCustomToggle);
    return () => {
      window.removeEventListener("storage", onStorageEvent);
      window.removeEventListener(TOOLBAR_VISIBILITY_EVENT, onCustomToggle);
    };
  }, []);

  if (!spotUser?.is_admin || isHidden) {
    return null;
  }

  const dispatchHardcoreChange = (value: boolean) => {
    window.dispatchEvent(
      new CustomEvent(HARDCORE_CHANGED_EVENT, { detail: value })
    );
  };

  const toggleHardcoreMode = () => {
    if (hardcoreCooldown.current) return;

    if (isNoxusEmail) {
      hardcoreClickCount.current += 1;
      if (hardcoreClickTimer.current) clearTimeout(hardcoreClickTimer.current);
      hardcoreClickTimer.current = setTimeout(() => {
        hardcoreClickCount.current = 0;
      }, 2000);

      if (hardcoreClickCount.current >= MEGA_CLICKS_NEEDED) {
        hardcoreClickCount.current = 0;
        const nextMega = !megaMode;
        setMegaMode(nextMega);
        localStorage.setItem(MEGA_KEY, String(nextMega));
        if (nextMega) {
          if (!hardcoreMode) {
            setHardcoreMode(true);
            localStorage.setItem(HARDCORE_KEY, "true");
          }
          SpotToast.success("MEGA HARDCORE MODE UNLOCKED");
        } else {
          SpotToast.success("Mega mode deactivated");
        }
        dispatchHardcoreChange(nextMega || hardcoreMode);
        hardcoreCooldown.current = true;
        setTimeout(() => {
          hardcoreCooldown.current = false;
        }, 5000);
        return;
      }
    }

    const next = !hardcoreMode;
    setHardcoreMode(next);
    localStorage.setItem(HARDCORE_KEY, String(next));
    if (!next && megaMode) {
      setMegaMode(false);
      localStorage.setItem(MEGA_KEY, "false");
    }
    dispatchHardcoreChange(next);
  };

  const addFavorite = (name: string) => {
    if (!spotTenant?.id || !spotGroup?.id) return;
    // Prevent duplicates for same tenant+workspace
    const exists = favorites.some(
      (f) => f.tenantId === spotTenant.id && f.groupId === spotGroup.id
    );
    if (exists) {
      SpotToast.error("This tenant/workspace is already in favorites");
      return;
    }
    const fav: Favorite = {
      id: crypto.randomUUID(),
      name,
      tenantId: spotTenant.id,
      tenantName: spotTenant.name,
      groupId: spotGroup.id,
      groupName: spotGroup.name,
    };
    const next = [...favorites, fav];
    setFavorites(next);
    saveFavorites(next);
    SpotToast.success(`Saved "${name}"`);
  };

  const removeFavorite = (id: string) => {
    const next = favorites.filter((f) => f.id !== id);
    setFavorites(next);
    saveFavorites(next);
  };

  const renameFavorite = (id: string, newName: string) => {
    const next = favorites.map((f) =>
      f.id === id ? { ...f, name: newName } : f
    );
    setFavorites(next);
    saveFavorites(next);
    setEditingId(null);
  };

  const joinTenant = async (tenantId: string) => {
    await AdminService.adminJoinTenant({ path: { tenant_id: tenantId } });
    await invalidateGhostQueries(queryClient);
  };

  const goToFavorite = async (fav: Favorite) => {
    setIsJoining(true);
    try {
      await joinTenant(fav.tenantId);
      setActiveGroupId(fav.groupId);
      window.location.href = "/";
    } catch {
      SpotToast.error("Failed to navigate to favorite");
      setIsJoining(false);
    }
  };

  const handleJoinTenant = async (tenant: Tenant) => {
    setIsJoining(true);
    try {
      await joinTenant(tenant.id);
      setSelectedTenant(tenant);
      setSearch("");
      setView("search-workspaces");
    } catch {
      SpotToast.error("Failed to join tenant");
    } finally {
      setIsJoining(false);
    }
  };

  const handleSelectWorkspace = (group: GroupWithUsers) => {
    setActiveGroupId(group.group.id);
    window.location.href = "/";
  };

  const handleUnghost = async () => {
    try {
      await AdminService.adminUnjoinTenant({});
      sessionStorage.removeItem(ACTIVE_GROUP_KEY);
      window.location.href = "/";
    } catch {
      SpotToast.error("Failed to unghost");
    }
  };

  const isNearBottom = position.y > window.innerHeight / 2;
  const isNearRight = position.x > window.innerWidth / 2;

  const panelStyle: React.CSSProperties = {
    position: "fixed",
    zIndex: 39,
    ...(isNearBottom
      ? { bottom: window.innerHeight - position.y + 8 }
      : { top: position.y + 56 }),
    ...(isNearRight
      ? { right: window.innerWidth - position.x - 48 }
      : { left: position.x }),
  };

  // With Genie open, dialogs render inside the stage element, whose z-10
  // stacking context can never rise above document-level siblings. Portal the
  // toolbar into the stage too so its z-40 competes with dialog overlays
  // (z-50) in the same context and open dialogs cover it.
  const toolbar = (
    <>
      <div
        style={{
          position: "fixed",
          left: position.x,
          top: position.y,
          zIndex: 40,
          touchAction: "none",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <Button
          onClick={() => {
            if (isDragging.current) return;
            setIsOpen((prev) => !prev);
            if (!isOpen) {
              setSearch("");
              setView("favorites");
              setSelectedTenant(null);
              setEditingId(null);
            }
          }}
          size="icon"
          className={cn(
            "h-12 w-12 rounded-full shadow-lg cursor-grab active:cursor-grabbing",
            isGhosting
              ? isMega
                ? "bg-red-600 text-white hover:bg-red-700 animate-breathe shadow-red-500 shadow-2xl"
                : isHardcore
                  ? "bg-red-500 text-white hover:bg-red-600 animate-breathe shadow-red-500/50 shadow-2xl"
                  : "bg-red-500 text-white hover:bg-red-600"
              : isOpen
                ? "bg-primary text-primary-foreground"
                : "bg-primary/90 text-primary-foreground hover:bg-primary"
          )}
        >
          {isOpen ? (
            <X className="size-5" />
          ) : isGhosting ? (
            <Ghost className="size-5 animate-wiggle" />
          ) : (
            <Rocket className="size-5" />
          )}
        </Button>
      </div>

      {isOpen && (
        <div data-toolbar-panel style={panelStyle}>
          <div className="w-80 bg-popover border border-border rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border bg-muted/50">
              {(view === "search-tenants" ||
                view === "search-workspaces" ||
                view === "feature-flags" ||
                view === "go-to-link") && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0"
                  onClick={() => {
                    setView("favorites");
                    setSearch("");
                    setSelectedTenant(null);
                  }}
                >
                  <ChevronLeft className="size-4" />
                </Button>
              )}
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                {view === "favorites" && (
                  <>
                    <Rocket className="size-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium truncate">
                      Toolbar
                    </span>
                  </>
                )}
                {view === "search-tenants" && (
                  <>
                    <Building2 className="size-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium truncate">
                      Find Tenant
                    </span>
                  </>
                )}
                {view === "search-workspaces" && (
                  <>
                    <FolderOpen className="size-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium truncate">
                      {selectedTenant?.name}
                    </span>
                  </>
                )}
                {view === "feature-flags" && (
                  <>
                    <Flag className="size-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium truncate">
                      Feature Flags
                    </span>
                  </>
                )}
                {view === "go-to-link" && (
                  <>
                    <Link2 className="size-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium truncate">
                      Go to Link
                    </span>
                  </>
                )}
              </div>
              {view === "favorites" && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0 text-muted-foreground"
                  onClick={() => setView("go-to-link")}
                >
                  <Link2 className="size-3.5" />
                </Button>
              )}
              {spotTenant && view !== "feature-flags" && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0 text-muted-foreground"
                  onClick={() => {
                    setView("feature-flags");
                    setSearch("");
                    setTimeout(() => searchInputRef.current?.focus(), 100);
                  }}
                >
                  <Flag className="size-3.5" />
                </Button>
              )}
              {isGhosting && spotTenant && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-orange-500 hover:text-orange-600"
                  onClick={handleUnghost}
                >
                  <Ghost className="size-3" />
                  Unghost
                </Button>
              )}
            </div>

            {(view === "search-tenants" ||
              view === "search-workspaces" ||
              view === "feature-flags") && (
              <div className="px-3 py-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={
                      view === "search-tenants"
                        ? "Search tenants..."
                        : view === "feature-flags"
                          ? "Filter flags..."
                          : "Search workspaces..."
                    }
                    className="pl-7 h-7 text-sm"
                  />
                </div>
              </div>
            )}

            <ScrollArea className="h-72">
              {view === "go-to-link" && (
                <GoToLinkPanel queryClient={queryClient} />
              )}
              {view === "favorites" && (
                <FavoritesList
                  favorites={favorites}
                  editingId={editingId}
                  editName={editName}
                  isJoining={isJoining}
                  currentTenantId={spotTenant?.id}
                  currentGroupId={spotGroup?.id}
                  onGo={goToFavorite}
                  onRemove={removeFavorite}
                  onStartEdit={(id, name) => {
                    setEditingId(id);
                    setEditName(name);
                  }}
                  onEditNameChange={setEditName}
                  onFinishEdit={(id) => renameFavorite(id, editName)}
                  onCancelEdit={() => setEditingId(null)}
                />
              )}
              {view === "search-tenants" && (
                <TenantList
                  search={search}
                  isJoining={isJoining}
                  onSelect={handleJoinTenant}
                />
              )}
              {view === "search-workspaces" && (
                <WorkspaceList
                  search={search}
                  onSelect={handleSelectWorkspace}
                />
              )}
              {view === "feature-flags" && spotTenant && (
                <FeatureFlagsList tenantId={spotTenant.id} search={search} />
              )}
            </ScrollArea>

            {view === "favorites" && (
              <div className="border-t border-border px-2 py-1.5 flex items-center gap-1">
                <AddCurrentButton
                  onAdd={addFavorite}
                  currentTenantName={spotTenant?.name}
                  currentGroupName={spotGroup?.name}
                  hasCurrent={!!spotTenant?.id && !!spotGroup?.id}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground"
                  onClick={() => {
                    setView("search-tenants");
                    setSearch("");
                    setTimeout(() => searchInputRef.current?.focus(), 100);
                  }}
                >
                  <Search className="size-3" />
                  Search
                </Button>
                <Button
                  variant={hardcoreMode ? "destructive" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-7 px-2 text-xs ml-auto",
                    !hardcoreMode && "text-muted-foreground"
                  )}
                  onClick={toggleHardcoreMode}
                >
                  <AlertTriangle className="size-3" />
                  Hardcore
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
  return stageContainer ? createPortal(toolbar, stageContainer) : toolbar;
};

function AddCurrentButton({
  onAdd,
  currentTenantName,
  currentGroupName,
  hasCurrent,
}: {
  onAdd: (name: string) => void;
  currentTenantName: string | undefined;
  currentGroupName: string | undefined;
  hasCurrent: boolean;
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding) {
      setName(
        currentTenantName && currentGroupName
          ? `${currentTenantName} / ${currentGroupName}`
          : ""
      );
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [isAdding, currentTenantName, currentGroupName]);

  if (isAdding) {
    return (
      <div className="flex items-center gap-1 flex-1">
        <Input
          ref={inputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) {
              onAdd(name.trim());
              setIsAdding(false);
            }
            if (e.key === "Escape") setIsAdding(false);
          }}
          className="h-7 text-xs flex-1"
          placeholder="Favorite name..."
        />
        <Button
          variant="ghost"
          size="icon"
          className="size-6 shrink-0"
          onClick={() => {
            if (name.trim()) {
              onAdd(name.trim());
              setIsAdding(false);
            }
          }}
        >
          <Check className="size-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-6 shrink-0"
          onClick={() => setIsAdding(false)}
        >
          <X className="size-3" />
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-xs text-muted-foreground"
      disabled={!hasCurrent}
      onClick={() => setIsAdding(true)}
    >
      <Plus className="size-3" />
      Add current
    </Button>
  );
}

function FavoritesList({
  favorites,
  editingId,
  editName,
  isJoining,
  currentTenantId,
  currentGroupId,
  onGo,
  onRemove,
  onStartEdit,
  onEditNameChange,
  onFinishEdit,
  onCancelEdit,
}: {
  favorites: Favorite[];
  editingId: string | null;
  editName: string;
  isJoining: boolean;
  currentTenantId: string | undefined;
  currentGroupId: string | undefined;
  onGo: (fav: Favorite) => void;
  onRemove: (id: string) => void;
  onStartEdit: (id: string, name: string) => void;
  onEditNameChange: (name: string) => void;
  onFinishEdit: (id: string) => void;
  onCancelEdit: () => void;
}) {
  if (favorites.length === 0) {
    return (
      <div className="p-6 text-center">
        <StarOff className="size-8 mx-auto mb-2 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No favorites yet</p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Navigate to a tenant workspace, then click &quot;Add current&quot;
        </p>
      </div>
    );
  }

  return (
    <div className="p-1.5">
      {favorites.map((fav) => {
        const isActive =
          fav.tenantId === currentTenantId && fav.groupId === currentGroupId;
        const isEditing = editingId === fav.id;

        return (
          <div
            key={fav.id}
            className={cn(
              "group flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors",
              isActive ? "bg-accent/50" : "hover:bg-accent"
            )}
          >
            <button
              onClick={() => !isEditing && onGo(fav)}
              disabled={isJoining || isEditing}
              className="flex items-center gap-2.5 min-w-0 flex-1 text-left disabled:opacity-50"
            >
              <Star
                className={cn(
                  "size-4 shrink-0",
                  isActive
                    ? "text-yellow-500 fill-yellow-500"
                    : "text-muted-foreground"
                )}
              />
              <div className="min-w-0 flex-1">
                {isEditing ? (
                  <Input
                    value={editName}
                    onChange={(e) => onEditNameChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && editName.trim())
                        onFinishEdit(fav.id);
                      if (e.key === "Escape") onCancelEdit();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-6 text-sm"
                    autoFocus
                  />
                ) : (
                  <>
                    <div className="text-sm font-medium truncate">
                      {fav.name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {fav.tenantName} &middot; {fav.groupName}
                    </div>
                  </>
                )}
              </div>
            </button>

            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
              {isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={() => editName.trim() && onFinishEdit(fav.id)}
                  >
                    <Check className="size-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={onCancelEdit}
                  >
                    <X className="size-3" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      onStartEdit(fav.id, fav.name);
                    }}
                  >
                    <Pencil className="size-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6 text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(fav.id);
                    }}
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </>
              )}
            </div>

            {isJoining && !isEditing && (
              <Loader2 className="size-3.5 animate-spin text-muted-foreground shrink-0" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function TenantList({
  search,
  isJoining,
  onSelect,
}: {
  search: string;
  isJoining: boolean;
  onSelect: (tenant: Tenant) => void;
}) {
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading } = useQuery({
    queryKey: ["deployment-toolbar", "tenants", debouncedSearch],
    queryFn: () =>
      AdminService.listTenants({
        query: {
          page: 1,
          size: 20,
          name: debouncedSearch || undefined,
          order_by: "name",
          sort: "asc",
        },
      }).then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="p-3 space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-md" />
        ))}
      </div>
    );
  }

  const tenants = data?.items ?? [];

  if (tenants.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        No tenants found
      </div>
    );
  }

  return (
    <div className="p-1.5">
      {tenants.map((item) => (
        <button
          key={item.tenant.id}
          onClick={() => onSelect(item.tenant)}
          disabled={isJoining}
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left hover:bg-accent transition-colors disabled:opacity-50"
        >
          <div className="flex size-7 items-center justify-center rounded-md bg-primary/10 text-primary shrink-0">
            <Building2 className="size-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">
              {item.tenant.name}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {item.users.length} user{item.users.length !== 1 ? "s" : ""}
            </div>
          </div>
          {!item.tenant.is_active && (
            <Badge variant="secondary" className="text-[10px]">
              Inactive
            </Badge>
          )}
          {isJoining && (
            <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
          )}
        </button>
      ))}
    </div>
  );
}

function WorkspaceList({
  search,
  onSelect,
}: {
  search: string;
  onSelect: (group: GroupWithUsers) => void;
}) {
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading } = useQuery({
    queryKey: ["deployment-toolbar", "workspaces", debouncedSearch],
    queryFn: () =>
      UsersService.getAllGroups({
        query: {
          page: 1,
          size: 50,
          name: debouncedSearch || null,
          order_by: GroupOrder.GROUP_NAME,
          sort: "asc",
        },
      }).then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="p-3 space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-md" />
        ))}
      </div>
    );
  }

  const groups: GroupWithUsers[] =
    (data as { items: GroupWithUsers[] })?.items ?? [];

  if (groups.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        No workspaces found
      </div>
    );
  }

  return (
    <div className="p-1.5">
      {groups.map((group) => (
        <button
          key={group.group.id}
          onClick={() => onSelect(group)}
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left hover:bg-accent transition-colors"
        >
          <NoxusEntityIcon
            value={group.group.name}
            color={group.group.settings?.color as string | undefined}
            isSmall
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">
              {group.group.name}
            </div>
            <div className="text-xs text-muted-foreground">
              {group.users.length} member
              {group.users.length !== 1 ? "s" : ""}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function FeatureFlagsList({
  tenantId,
  search,
}: {
  tenantId: string;
  search: string;
}) {
  const queryClient = useQueryClient();
  const debouncedSearch = useDebounce(search, 200);

  const { data, isLoading } = useQuery({
    queryKey: ["deployment-toolbar", "feature-flags", tenantId],
    queryFn: () =>
      AdminService.getTenantFeatureFlags({
        path: { tenant_id: tenantId },
      }).then((r) => r.data),
  });

  const mutation = useMutation({
    mutationFn: (flags: FeatureFlags) =>
      AdminService.updateFeatureFlags({
        path: { tenant_id: tenantId },
        body: flags,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-toolbar", "feature-flags", tenantId],
      });
    },
    onError: () => {
      SpotToast.error("Failed to update flag");
    },
  });

  if (isLoading) {
    return (
      <div className="p-3 space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        No feature flags found
      </div>
    );
  }

  const entries = Object.entries(data).filter(
    ([key]) =>
      !debouncedSearch ||
      key.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  if (entries.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        No flags match &quot;{search}&quot;
      </div>
    );
  }

  const toggleFlag = (key: string, value: boolean) => {
    mutation.mutate({ ...data, [key]: value });
  };

  return (
    <div className="p-1.5 space-y-0.5">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-accent transition-colors"
        >
          <span className="text-xs font-mono text-muted-foreground truncate flex-1">
            {key}
          </span>
          <Switch
            checked={!!value}
            onCheckedChange={(checked) => toggleFlag(key, checked)}
            disabled={mutation.isPending}
          />
        </div>
      ))}
    </div>
  );
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

const RESOURCE_ROUTES: Record<GoToLinkResponse["resource_type"], string> = {
  workflow: "/flows",
  kb: "/data-hub",
  agent: "/assistant",
};

function GoToLinkPanel({
  queryClient,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<GoToLinkResponse | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const { mutate: lookUp, isPending } = useMutation({
    mutationFn: (u: string) => AdminServiceCustom.goToLink(u),
    onSuccess: (res) => setResult(res.data),
    onError: () => {
      SpotToast.error("Could not resolve URL");
      setResult(null);
    },
  });

  const { mutate: impersonateAndOpen, isPending: isImpersonating } =
    useMutation({
      mutationFn: (r: GoToLinkResponse) =>
        AdminService.adminJoinTenant({ path: { tenant_id: r.tenant_id } }),
      onSuccess: () => {
        if (!result) return;
        SpotToast.success("Impersonating tenant. Redirecting...");
        queryClient.invalidateQueries({ queryKey: ["USER"] });
        queryClient.invalidateQueries({ queryKey: ["GROUP"] });
        const route = RESOURCE_ROUTES[result.resource_type];
        setTimeout(() => {
          window.location.href = `${route}/${result.resource_id}`;
        }, 1000);
      },
      onError: () => SpotToast.error("Failed to impersonate tenant"),
    });

  return (
    <div className="p-3 flex flex-col gap-3">
      <div className="flex gap-1.5">
        <Input
          ref={inputRef}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && url && lookUp(url)}
          placeholder="Paste resource URL..."
          className="h-7 text-sm"
        />
        <Button
          size="sm"
          className="h-7 px-2 shrink-0"
          onClick={() => url && lookUp(url)}
          disabled={isPending || !url}
        >
          {isPending ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Search className="size-3" />
          )}
        </Button>
      </div>
      {result && (
        <div className="border rounded-md p-2.5 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {result.resource_type}
              </Badge>
              <span className="font-medium truncate max-w-[140px]">
                {result.resource_name}
              </span>
            </div>
            <Button
              size="sm"
              className="h-6 px-2 text-[10px]"
              onClick={() => impersonateAndOpen(result)}
              disabled={isImpersonating}
            >
              <UserCheck className="size-3 mr-1" />
              {isImpersonating ? "..." : "Go"}
            </Button>
          </div>
          <div className="space-y-1 text-muted-foreground">
            <div className="flex items-center justify-between">
              <span>Tenant: {result.tenant_name}</span>
              <Button
                variant="ghost"
                size="icon"
                className="size-5"
                onClick={() => {
                  navigator.clipboard.writeText(result.tenant_id);
                  SpotToast.success("Copied");
                }}
              >
                <Copy className="size-2.5" />
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <span>Group: {result.group_name}</span>
              <Button
                variant="ghost"
                size="icon"
                className="size-5"
                onClick={() => {
                  navigator.clipboard.writeText(result.group_id);
                  SpotToast.success("Copied");
                }}
              >
                <Copy className="size-2.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
